import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const beausoleilEn: ChauffeurPage = {
  slug: "beausoleil",
  title: "VTC & private chauffeur in Beausoleil",
  subtitle:
    "Private chauffeur in Beausoleil: the town above Monte-Carlo, its street-line border with Monaco, its Belle Époque buildings and a ~35 min transfer to Nice airport.",
  keywords: [
    "vtc beausoleil",
    "private chauffeur beausoleil",
    "beausoleil monaco chauffeur",
    "beausoleil nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Beausoleil",
    subtitle:
      "The French balcony above Monte-Carlo: transfers into Monaco on foot or by car, in a steep town with a Belle Époque heritage.",
    keywords: [
      "VTC Beausoleil",
      "Monte-Carlo chauffeur",
      "Nice airport transfer",
      "Belle Époque chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Beausoleil",
    badges: [
      "Above Monte-Carlo",
      "Belle Époque buildings",
      "~35 min from the airport",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Beausoleil",
    items: [
      {
        title: "Above Monte-Carlo",
        description:
          "Here the border runs along a street: the chauffeur drops you at the edge of the Principality, from where Monte-Carlo is reached on foot, without diving into Monaco’s traffic.",
      },
      {
        title: "Steep streets & Belle Époque",
        description:
          "A vertical town of sharp gradients, stairways and public lifts: a chauffeur used to the steep climbs right up to the foot of your building.",
      },
      {
        title: "A balcony over the Principality",
        description:
          "From the heights of Beausoleil, Monaco unfolds below: short links to Monte-Carlo, the Rock and Fontvieille.",
      },
      {
        title: "~35 minutes from the airport",
        description:
          "A direct transfer to and from Nice airport, via the middle corniche or the A8, with flight tracking.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Beausoleil",
    services: [
      {
        title: "Transfers into Monaco",
        text: "Drop-off at the border or in the heart of Monte-Carlo for your appointments, with no parking to sort out in the Principality.",
      },
      {
        title: "Nice airport transfers",
        text: "Beausoleil ⇄ Nice airport in about 35 minutes, with flight tracking and a personal welcome.",
      },
      {
        title: "Upper-town runs",
        text: "Pick-up at the foot of the steep buildings and the stepped streets, down to the lower town or to Monaco.",
      },
      {
        title: "Monaco events",
        text: "Beausoleil overlooks the circuit: during the Grand Prix and galas, shuttles and drop-off as close as possible to the entrances.",
      },
      {
        title: "Hourly hire & corniches",
        text: "By the hour or by the day to link Monaco, Cap-d’Ail, Èze and Nice along the corniches.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Boulevard de France",
        description:
          "The border street between Beausoleil and Monaco, one pavement per country.",
      },
      {
        name: "Riviera Palace",
        description:
          "A Belle Époque building and its glass hall, an emblem of the town.",
      },
      {
        name: "Lifts to Monte-Carlo",
        description: "Public access linking the upper town to Monaco’s level.",
      },
      {
        name: "Place de la Libération",
        description: "Beausoleil’s lively centre, shops and market.",
      },
      {
        name: "Stepped streets",
        description:
          "Climbs and alleys between the levels of the sloping town.",
      },
      {
        name: "Middle Corniche",
        description: "The overhanging road linking Nice, Èze and Monaco.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur for the balcony over Monte-Carlo",
    subheading:
      "Beausoleil climbs above Monaco, where the border fits within a street and the town rises in tiers.",
    paragraphs: [
      "Clinging to the slope above Monte-Carlo, Beausoleil shares with Monaco a border so slight that it runs down the middle of a street. You descend into the Principality on foot or by the public lifts; for the rest, a chauffeur absorbs the steep gradients and drops you as close as possible, at the top of the town as at the bottom.",
      "KS Rent Services applies fixed fares set in advance — including Beausoleil ⇄ Nice airport — and hourly or daily hire, with an immediate quote. Our bilingual French/English chauffeurs, based on the French Riviera, are rated 5.0/5 from 38 Google reviews.",
      "The Belle Époque buildings, like the Riviera Palace, recall the resort’s golden age; today Beausoleil serves above all as a base above Monaco, from which the chauffeur reaches Monte-Carlo, the Rock or Fontvieille in a few minutes.",
    ],
    bullets: [
      "Drop-off at the border, Monte-Carlo on foot",
      "Fixed fares & hourly hire, immediate quote",
      "Bilingual FR/EN chauffeurs, 5.0/5 from 38 Google reviews",
    ],
  },
  process: {
    heading: "Book a chauffeur in Beausoleil",
    steps: [
      "Share your programme: Monaco, airport transfer, upper-town run or an event.",
      "We confirm the vehicle and the fixed fare or the hourly hire.",
      "The chauffeur picks you up at the foot of your building, even in the steep streets.",
      "Live adjustments to the traffic and the access points into Monaco.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Beausoleil"),
    {
      q: "How long does it take to reach Nice airport?",
      a: "About 35 minutes via the middle corniche or the A8. The chauffeur tracks your flight and adjusts the departure time for traffic on the approach to Nice.",
    },
    {
      q: "How do transfers into Monaco work from Beausoleil?",
      a: "The border runs along a Beausoleil street: we drop you at the edge to reach Monte-Carlo on foot, or take you straight into the heart of the Principality depending on your appointment.",
    },
    {
      q: "Do you operate during the Monaco Grand Prix?",
      a: "Yes. Beausoleil overlooks the circuit; during the Grand Prix and galas we run shuttles and drop off as close as possible to the entrances despite the closures.",
    },
    {
      q: "Can you come right up to the foot of the steep buildings?",
      a: "Yes. Our chauffeurs know the sharp climbs and stepped streets of Beausoleil and come to collect you as close as possible to your entrance.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to the residents and visitors who reach Monaco day in, day out.",
    },
    {
      q: "Which vehicles do you offer for Beausoleil?",
      a: "A Mercedes sedan, a 7-seat van or an SUV depending on the number of passengers, at ease in the steep streets of the upper town.",
    },
  ],
  relatedLinks: [
    { label: "Private chauffeur in Monaco", href: "/en/chauffeur/monaco" },
    { label: "Nice Airport shuttle", href: "/en/chauffeur/airport" },
    { label: "Private car service in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Car service on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your private chauffeur in Beausoleil",
    text: "Transfers into Monaco, an airport run or an upper-town shuttle: book your chauffeur.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Beausoleil — private chauffeur & transfers",
    description:
      "Private car service in Beausoleil: above Monte-Carlo, transfers into Monaco, Belle Époque buildings and a ~35 min transfer to Nice airport.",
    canonical: "/en/chauffeur/beausoleil",
  },
};

export default beausoleilEn;
